// Production deployment configuration and utilities

export const DEPLOYMENT_CONFIG = {
  // App version for cache busting and tracking
  version: '1.0.0',
  buildTime: new Date().toISOString(),
  
  // Feature flags
  features: {
    enableAnalytics: true,
    enableErrorTracking: true,
    enablePerformanceMonitoring: true,
    enablePWA: true,
    enablePushNotifications: true,
    enableOfflineMode: true,
    enableChatbot: true,
    enableSocialProof: true,
  },
  
  // Cache configuration
  cache: {
    staticAssets: 7 * 24 * 60 * 60, // 7 days
    apiResponses: 5 * 60, // 5 minutes
    images: 30 * 24 * 60 * 60, // 30 days
  },
  
  // API configuration
  api: {
    timeout: 30000, // 30 seconds
    retries: 3,
    retryDelay: 1000, // 1 second base delay
  },
  
  // Rate limiting
  rateLimit: {
    maxRequests: 100,
    windowMs: 60 * 1000, // 1 minute
  },
} as const;

// Environment detection
export const getEnvironment = (): 'development' | 'staging' | 'production' => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }
  
  return 'production';
};

// Check if we're in production
export const isProduction = (): boolean => getEnvironment() === 'production';

// Check if we're in development
export const isDevelopment = (): boolean => getEnvironment() === 'development';

// Feature flag checker
export const isFeatureEnabled = (feature: keyof typeof DEPLOYMENT_CONFIG.features): boolean => {
  return DEPLOYMENT_CONFIG.features[feature];
};

// Version info for debugging
export const getVersionInfo = () => ({
  version: DEPLOYMENT_CONFIG.version,
  buildTime: DEPLOYMENT_CONFIG.buildTime,
  environment: getEnvironment(),
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
});

// Logging utility that respects environment
export const logger = {
  debug: (...args: unknown[]) => {
    if (isDevelopment()) {
      console.debug('[DEBUG]', ...args);
    }
  },
  info: (...args: unknown[]) => {
    console.info('[INFO]', ...args);
  },
  warn: (...args: unknown[]) => {
    console.warn('[WARN]', ...args);
  },
  error: (...args: unknown[]) => {
    console.error('[ERROR]', ...args);
    // In production, you'd send this to an error tracking service
  },
};

// Request timeout wrapper
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number = DEPLOYMENT_CONFIG.api.timeout
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Request timed out after ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
};

// Retry wrapper with exponential backoff
export const withRetry = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = DEPLOYMENT_CONFIG.api.retries,
  baseDelay: number = DEPLOYMENT_CONFIG.api.retryDelay
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError!;
};

// Memory cleanup utility
export const cleanupResources = () => {
  // Clear any cached data that's no longer needed
  if (typeof window !== 'undefined') {
    // Clear old local storage entries
    const keysToCheck = ['temp_', 'cache_', 'expired_'];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && keysToCheck.some(prefix => key.startsWith(prefix))) {
        try {
          const item = JSON.parse(localStorage.getItem(key) || '{}');
          if (item.expiresAt && new Date(item.expiresAt) < new Date()) {
            localStorage.removeItem(key);
          }
        } catch {
          // Invalid JSON, remove it
          localStorage.removeItem(key);
        }
      }
    }
    
    // Force garbage collection if available
    if ((window as any).gc) {
      (window as any).gc();
    }
  }
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/icon-192.png',
    '/icon-512.png',
  ];
  
  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Service worker registration with update handling
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    logger.info('Service Worker not supported');
    return null;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    logger.info('Service workers cleared to prevent stale app bundles');
    return null;
  } catch (error) {
    logger.error('Service Worker cleanup failed:', error);
    return null;
  }
};

// Initialize production monitoring
export const initializeProduction = () => {
  if (typeof window === 'undefined') return;
  
  // Preload critical resources
  preloadCriticalResources();
  
  // Service workers are intentionally not registered; stale cached app bundles
  // previously kept showing old removed UI after updates.
  
  // Cleanup old resources periodically
  setInterval(cleanupResources, 30 * 60 * 1000); // Every 30 minutes
  
  // Log version info
  logger.info('App initialized:', getVersionInfo());
};
